import APIClient from '../state-charts/api-client';
import session from '../lib/session.js';
import { TS } from '../config/translation-keys.js';
import { t9n } from '../lib/translate.js';
import { format_date } from '../lib/utils';

// model classes  ---------------------------------------------------------- //

let Comment = class {
  constructor(comment) {
    this._data = comment;
  }

  get display_name() {
    if (!!this._data.partner.organization_name) {
      return this._data.partner.organization_name;
    }
    return `${this._data.partner.first_name}`;
  }

  get avatar_url() {
    return `${this._data.partner.image_url}`;
  }

  get display_date() {
    return new Date(this._data.date).toLocaleDateString();
  }

  get rating() {
    return this._data.rating;
  }

  get rating_image_url() {
    return `/assets/rating/rate_${this.rating}.svg`;
  }

  get message() {
    return this._data.message || 'no message';
  }
};

let Feedback = class {
  constructor(data) {
    this.comments = data.feedbacks;
    this.paging_info = data.results_info;
  }

  get has_more_comments() {
    return this.paging_info.current_page < this.paging_info.number_of_pages;
  }

  get no_comment_text() {
    return t9n(TS.FEEDBACK_LIST_NO_COMMENT_TEXT);
  }

  get load_more_label() {
    return t9n(TS.FEEDBACK_LIST_LOAD_MORE_LABEL);
  }

  set comments(feedback) {
    this._comments = feedback.map(c => new Comment(c));
  }

  get comments() {
    return this._comments;
  }

  set paging_info(data) {
    this._paging_info = data;
  }

  get paging_info() {
    return this._paging_info;
  }
};

let Profile = class {
  constructor(user, role, feedback) {
    this._user = user;
    this._role = role;
    this._merchant = get_merchant(this._user);
    this.feedback = feedback;
  }

  get is_orderer() {
    return this._role === 'orderer';
  }

  get is_worker() {
    return this._role === 'worker';
  }

  get order_uuid() {
    return this._orderid;
  }

  // translations ---------------------------------------------------------- //

  get header_role_text() {
    if (!!this._user.organization_name) {
      return this._user.organization_name;
    }
    return '';
    // return `${this._user.organization_name}`;
  }

  get date_label() {
    return t9n(TS.FEEDBACK_LIST_TODAY_LABEL);
  }

  get role_title() {
    return t9n(TS[`FEEDBACK_LIST_TITLE_${this._role.toUpperCase()}`]);
  }

  get worker_info_label() {
    return t9n(TS.PROFILE_WORKER_INFO_LABEL);
  }

  get description_label() {
    return t9n(TS.PROFILE_DESCRIPTION_LABEL);
  }

  get city_label() {
    return t9n(TS.PROFILE_CITY_LABEL);
  }

  get postal_code_label() {
    return t9n(TS.PROFILE_POSTAL_CODE_LABEL);
  }

  get email_label() {
    return t9n(TS.PROFILE_EMAIL_LABEL);
  }

  get company_info_label() {
    return t9n(TS.PROFILE_COMPAMY_LABEL);
  }

  get phone_number_label() {
    return t9n(TS.PROFILE_PHONE_NUMBER_LABEL);
  }

  get available_for_label() {
    return t9n(TS.PROFILE_AVAILABLE_FOR_LABEL);
  }

  get completed_label() {
    return this.is_orderer
      ? t9n(TS.PROFILE_COMPLETED_ORDERS_LABEL)
      : t9n(TS.PROFILE_COMPLETED_TASKS_LABEL);
  }

  get rating_label() {
    return t9n(TS.PROFILE_RATING_LABEL);
  }

  get cv_label() {
    return t9n(TS.PROFILE_CV_LINK_LABEL);
  }

  get cv_link_text() {
    const link = this.cv_url;
    return link ? t9n(TS.PROFILE_CV_LINK_TEXT) : '-';
  }

  get linkedin_label() {
    return t9n(TS.PROFILE_LINKEDIN_LINK_LABEL);
  }

  get referee_label() {
    return t9n(TS.PROFILE_REFEREE_LABEL);
  }

  get referee_text() {
    return this.referee;
  }

  get linkedin_link_text() {
    const link = this.linkedin_url;
    return link ? t9n(TS.PROFILE_LINKEDIN_LINK_TEXT) : '-';
  }

  // ----------------------------------------------------------------------- //

  get user() {
    return this._user;
  }

  get unread_chat_messages_count() {
    return get_unread_chat_messages_count();
  }

  get display_name() {
    if (
      this._user.organization_type !== 'organization_type_private_person' &&
      !!this._user.organization_name
    ) {
      return this._user.organization_name;
    }

    return `${this._user.first_name} ${this._user.last_name}`;
  }

  get role() {
    return this._role;
  }

  get avatar_url() {
    return `${this._user.image_url}`;
  }

  get profile_image_url() {
    return `${this._user.image_url}`;
  }

  get email() {
    if (!!this._user.email) {
      return this._user.email;
    }
    return t9n(TS.PROFILE_ONLY_FOR_WORKER_TEXT);
  }

  get address() {
    return this._user.address.primary_address;
  }

  get city() {
    return this._user.address.city;
  }

  get postal_code() {
    return this._user.address.postal_code;
  }

  get phone_number() {
    if (!!this._user.phone) {
      return `${this._user.phone.code} ${this._user.phone.number}`;
    }
    return t9n(TS.PROFILE_ONLY_FOR_WORKER_TEXT);
  }

  get description() {
    return this._user[`short_description_${this._role}`] || '-';
  }

  get rating() {
    let number = this._user.stats[`${this._role}_overall_rating`];
    let sign = number > 0 ? '+' : number < 0 ? '-' : '';
    return `${sign}${number}`;
  }

  get completed() {
    return this._user.stats[`${this._role}_completed_orders`];
  }

  get worker_type() {
    return this._user.worker_type;
  }

  get company_info() {
    return this._user.organization_name;
  }

  get company_number() {
    return this._user.company_registration_number;
  }

  get available_for() {
    let language = this._user.language;
    let all_tags = this._merchant.product_tags;
    let user_tags = all_tags.filter(t =>
      this._user.product_tags.includes(t.tag_id)
    );

    return user_tags.map(t => t.localized_names[`translation_${language}`]);
  }

  get is_cv_public() {
    return this._user.cv_is_public;
  }

  get cv_url() {
    return `/cv/worker/${this._user.id}`;
  }

  get linkedin_url() {
    return this._user.linked_in_link;
  }

  get referee() {
    return this._user.referee;
  }

  set feedback(feedback) {
    this._feedback = new Feedback(feedback);
  }

  get feedback() {
    return this._feedback;
  }

  set orderid(newid) {
    this._orderid = newid;
  }
};

// data loaders ------------------------------------------------------------ //

const get_unread_chat_messages_count = () => {
  return 0;
};

const get_merchant = () => {
  return session.value().merchant;
};

const load_profile = async () => {
  let user = session.value().user;
  let role = user.last_role;
  let data = {number_of_items_per_page: 200}
  let feedback = await APIClient.get_feedbacks(role, data);

  return new Profile(user, role, feedback);
};

const load_orderer_profile = async (order, user, role, age_range = null) => {
  let feedbacks = await APIClient.get_orderer_feedbacks({
    order_uuid: order.order_uuid,
    user_id: user.basic_profile.user_id,
    role: role,
    number_of_items_per_page: 200
  });

  let stats = {
    ...user.orderer_profile.stats,
    worker_overall_rating: user.orderer_profile.overall_rating,
  };

  delete stats.overall_rating;

  user = {
    ...user.basic_profile,
    ...user.orderer_profile,
    stats: stats,
    order: order,
    age_range: `age_range_${age_range}`,
    merchant_uuid: session.value().user.merchant_uuid,
  };

  return new Profile(user, role, feedbacks);
};

const load_worker_profile = async (order, user, role) => {
  let feedbacks = await APIClient.get_worker_feedbacks({
    order_uuid: order.order_uuid,
    user_id: user.basic_profile.user_id,
    role: role,
    number_of_items_per_page: 200
  });

  // let product_tags = user.worker_profile.product_tags;

  let stats = {
    ...user.worker_profile.stats,
    worker_overall_rating: user.worker_profile.overall_rating,
  };

  delete stats.overall_rating;

  user = {
    ...user.basic_profile,
    ...user.worker_profile,
    stats: stats,
    order: order,
    // product_tags: `product_tags,
    merchant_uuid: session.value().user.merchant_uuid,
  };

  return new Profile(user, role, feedbacks);
};

const load_applicant_profile = async (order, user, role) => {
  let feedbacks = await APIClient.get_applicant_feedbacks({
    order_uuid: order.order_uuid,
    user_id: user.basic_profile.user_id,
    role: role,
    number_of_items_per_page: 200
  });

  let stats = {
    ...user.worker_profile.stats,
    worker_overall_rating: !!user.worker_profile.overall_rating ? user.worker_profile.overall_rating : 0,
  };

  delete stats.overall_rating;

  user = {
    ...user.basic_profile,
    ...user.worker_profile,
    stats: stats,
    order: order,
    merchant_uuid: session.value().user.merchant_uuid,
  };

  return new Profile(user, role, feedbacks);
};

const load_profile_for_order = async (order_uuid, user_id, age_range = null) => {
  let order = await APIClient.get_order({ order_uuid: order_uuid });
  let is_orderer = order.orderer.basic_profile.user_id.toString() === user_id;
  let is_worker =
    order.worker && order.worker.basic_profile.user_id.toString() === user_id;
  let user;
  let role;

  if (is_orderer) {
    user = order.orderer;
    role = 'orderer';
    return await load_orderer_profile(order, user, role, age_range);
  } else if (is_worker) {
    user = order.worker;
    role = 'worker';
    return await load_worker_profile(order, user, role);
  } else {
    let applicants = order.applications.map(a => a.worker);
    user = applicants.find(a => a.basic_profile.user_id.toString() === user_id);
    role = 'worker';
    return await load_applicant_profile(order, user, role);
  }
};

// exports ----------------------------------------------------------------- //

export default {
  Comment,
  Feedback,
  Profile,

  load_profile,
  load_profile_for_order,
};
